import { useState, useRef, useEffect } from 'react';
import { toUserZoned } from '../../../shared/lib/tzDate';
import { calendarApi } from '../../calendar/api';
import { getErrorMessage } from '../../../shared/errors/getErrorMessage';
import {
    TIME_SLOTS, getGanttConfig, calculateTaskGanttPos,
    calculateDropToTaskUpdate, calculateResizeToTaskUpdate,
} from '../lib/taskGrid';

// Extraído de `ComponentTaskHoy.jsx` (02.1-b6-taskhoy-orchestrator.md, paso C1) -- el motor de
// interacción del Gantt: drag & drop (snapped a 5 minutos) + resize de barra + la línea vertical
// de "hora actual" (cálculo + auto-scroll para centrarla). Mismo patrón que
// `useFlowCanvasInteraction.js` (roadmap del canvas de tareas, misma sesión): recibe lo que el
// container ya tiene (datos + los 3 verbos de persistencia) y devuelve estado + handlers.
export function useTaskGanttInteraction({
    timeRange, currentRange, userTz, tasks, calendarEvents, ganttTasks, selectedCalendarDay,
    toast, updateTaskInDB, patchTasksCalendarEvent, invalidateTasksCalendarEvents,
}) {
    // Gantt Drag & Drop States
    const [draggingTaskId, setDraggingTaskId] = useState(null);
    const [dragOverCol, setDragOverCol] = useState(null);

    // Current time vertical white line state
    const [currentTimeOffset, setCurrentTimeOffset] = useState(null);
    const ganttContainerRef = useRef(null);

    // Gantt Resize States
    const [isResizing, setIsResizing] = useState(false);
    const [resizingTask, setResizingTask] = useState(null);
    const [resizeStartWidth, setResizeStartWidth] = useState(0);
    const [resizeStartX, setResizeStartX] = useState(0);

    // Precise Gantt positioning states (5-minute snapped drag & drop)
    const [dragOverLeft, setDragOverLeft] = useState(null);
    const [dragOffsetX, setDragOffsetX] = useState(0);

    // Calculate current time offset inside Gantt
    useEffect(() => {
        const updateOffset = () => {
            const now = toUserZoned(new Date(), userTz);
            const hour = now.getHours();
            const min = now.getMinutes();

            const startHour = 8;
            const endHour = 20;

            const totalMinutesStart = startHour * 60;
            const totalMinutesEnd = endHour * 60;
            const totalMinutesNow = hour * 60 + min;

            if (totalMinutesNow >= totalMinutesStart && totalMinutesNow <= totalMinutesEnd) {
                const elapsed = totalMinutesNow - totalMinutesStart;
                const offset = (elapsed / (12 * 60)) * 1920; // 1920 px
                setCurrentTimeOffset(offset);
            } else if (totalMinutesNow < totalMinutesStart) {
                setCurrentTimeOffset(0);
            } else {
                setCurrentTimeOffset(1920);
            }
        };

        updateOffset();
        const interval = setInterval(updateOffset, 30000); // 30s
        return () => clearInterval(interval);
    }, [userTz]);

    // Auto-scroll to center on current time indicator
    useEffect(() => {
        if (currentTimeOffset !== null && ganttContainerRef.current) {
            setTimeout(() => {
                if (ganttContainerRef.current) {
                    const containerWidth = ganttContainerRef.current.clientWidth;
                    ganttContainerRef.current.scrollLeft = currentTimeOffset - (containerWidth / 2);
                }
            }, 100);
        }
    }, [currentTimeOffset]);

    // Pointer-events resize handles
    const handleResizeStart = (e, task) => {
        e.stopPropagation();
        e.preventDefault();
        setIsResizing(true);
        setResizingTask(task);

        const cfg = getGanttConfig(timeRange, currentRange.startStr, currentRange.endStr);
        const { widthSize } = calculateTaskGanttPos(task, cfg, currentRange.startStr, userTz);
        setResizeStartWidth(widthSize);
        setResizeStartX(e.clientX);

        try {
            e.target.setPointerCapture(e.pointerId);
        } catch(err) {}
    };

    const handleResizeMove = (e) => {
        if (!isResizing || !resizingTask) return;
        const deltaX = e.clientX - resizeStartX;
        const cfg = getGanttConfig(timeRange, currentRange.startStr, currentRange.endStr);
        const stepWidth = cfg.mode === 'hoy' ? (cfg.subColWidth / 6) : (cfg.subColWidth / 4);

        const newWidth = Math.max(stepWidth, resizeStartWidth + deltaX);
        const snappedWidth = Math.max(stepWidth, Math.round(newWidth / stepWidth) * stepWidth);

        const element = document.querySelector(`.gantt-bar-tag[data-task-id="${resizingTask.id}"]`);
        if (element) {
            element.style.width = `${snappedWidth}px`;
        }
    };

    const handleResizeEnd = (e, pointerId) => {
        if (!isResizing || !resizingTask) return;
        try {
            e.target.releasePointerCapture(pointerId);
        } catch(err) {}

        setIsResizing(false);
        const element = document.querySelector(`.gantt-bar-tag[data-task-id="${resizingTask.id}"]`);
        if (element) {
            const finalWidth = element.getBoundingClientRect().width;

            const cfg = getGanttConfig(timeRange, currentRange.startStr, currentRange.endStr);
            const stepWidth = cfg.mode === 'hoy' ? (cfg.subColWidth / 6) : (cfg.subColWidth / 4);
            const snappedWidth = Math.max(stepWidth, Math.round(finalWidth / stepWidth) * stepWidth);

            const computedDurationHours = calculateResizeToTaskUpdate(snappedWidth, cfg);

            const startD = new Date(resizingTask.start_time || resizingTask.start || Date.now());
            const endD = new Date(startD.getTime() + (computedDurationHours * 3600000));
            const newEndTimeIso = endD.toISOString();

            const realTaskId = resizingTask.original_id || resizingTask.id;
            const evId = resizingTask.event_id;
            patchTasksCalendarEvent(evId, { end_time: newEndTimeIso });
            Promise.resolve(updateTaskInDB(realTaskId, { end_time: newEndTimeIso })).then(invalidateTasksCalendarEvents);
        }
        setResizingTask(null);
    };

    // HTML5 Drag and Drop Handlers for Gantt Timeline Snapped to 5 Minutes
    const handleDragStart = (e, id) => {
        setDraggingTaskId(id);
        e.dataTransfer.setData("text/plain", id);

        // Find cursor offset within the dragged Gantt bar element
        const targetTag = e.target.closest('.gantt-bar-tag');
        if (targetTag) {
            const rect = targetTag.getBoundingClientRect();
            const offsetX = e.clientX - rect.left;
            setDragOffsetX(offsetX);
            e.dataTransfer.setData("dragOffsetX", offsetX.toString());
        } else {
            setDragOffsetX(0);
            e.dataTransfer.setData("dragOffsetX", "0");
        }
    };

    const handleDragOver = (e, colIndex) => {
        e.preventDefault();
        setDragOverCol(colIndex);
    };

    const handleDrop = (e, colIndex) => {
        e.preventDefault();
        const taskId = e.dataTransfer.getData("text/plain") || draggingTaskId;
        if (taskId) {
            const task = tasks.find(t => t.id === taskId);
            if (task) {
                const durationCols = task.duration_cols !== undefined ? task.duration_cols : 2;
                const newStartCol = Math.max(0, Math.min(colIndex, TIME_SLOTS.length - durationCols));
                updateTaskInDB(taskId, { start_col: newStartCol, date: selectedCalendarDay });
            }
        }
        setDraggingTaskId(null);
        setDragOverCol(null);
        setDragOverLeft(null);
        setDragOffsetX(0);
    };

    const handleDragEnd = () => {
        setDraggingTaskId(null);
        setDragOverCol(null);
        setDragOverLeft(null);
        setDragOffsetX(0);
    };

    const handleGanttRowDragOver = (e) => {
        e.preventDefault();
        const rect = e.currentTarget.getBoundingClientRect();
        const clientX = e.clientX;

        const cfg = getGanttConfig(timeRange, currentRange.startStr, currentRange.endStr);
        let leftX = clientX - rect.left - dragOffsetX;

        const stepWidth = cfg.mode === 'hoy' ? (cfg.subColWidth / 6) : (cfg.subColWidth / 4);
        let snappedLeft = Math.round(leftX / stepWidth) * stepWidth;
        snappedLeft = Math.max(0, snappedLeft);

        setDragOverLeft(snappedLeft);
    };

    const handleGanttRowDrop = (e) => {
        e.preventDefault();
        const rect = e.currentTarget.getBoundingClientRect();
        const clientX = e.clientX;

        const taskId = e.dataTransfer.getData("text/plain") || draggingTaskId;
        if (taskId) {
            let isCalendarEventDrop = false;
            let eventOriginalId = null;
            let task = ganttTasks.find(t => t.id === taskId) || tasks.find(t => t.id === taskId);
            let realTaskId = task?.original_id || taskId;

            if (!task && taskId.startsWith('event-')) {
                eventOriginalId = taskId.replace('event-', '');
                // Try to find the exact original ID or base ID for recurrences
                task = calendarEvents.find(e => String(e.id) === eventOriginalId || String(e.id) === eventOriginalId.split('_')[0]);
                if (task) isCalendarEventDrop = true;
            }

            if (task) {
                const dragOffsetVal = parseFloat(e.dataTransfer.getData("dragOffsetX") || dragOffsetX.toString() || "0");
                let leftX = clientX - rect.left - dragOffsetVal;

                const cfg = getGanttConfig(timeRange, currentRange.startStr, currentRange.endStr);
                const stepWidth = cfg.mode === 'hoy' ? (cfg.subColWidth / 6) : (cfg.subColWidth / 4);
                let snappedLeft = Math.round(leftX / stepWidth) * stepWidth;

                const maxLeft = 4000;
                snappedLeft = Math.max(0, Math.min(snappedLeft, maxLeft));

                const startDate = new Date(task.start_time || task.start || Date.now());
                const endDate = new Date(task.end_time || task.end || Date.now() + 3600000);
                const durationHours = (endDate.getTime() - startDate.getTime()) / 3600000;

                const { date: newDate, start_time: newStartIso, end_time: newEndIso } = calculateDropToTaskUpdate(snappedLeft, cfg, currentRange.startStr, durationHours, userTz);

                if (isCalendarEventDrop) {
                    patchTasksCalendarEvent(task.id, { start_time: newStartIso, end_time: newEndIso });

                    calendarApi.updateEvent(eventOriginalId, {
                        start_time: newStartIso,
                        end_time: newEndIso
                    }).then(invalidateTasksCalendarEvents).catch(err => {
                        console.error("Error updating event", err);
                        toast.error(getErrorMessage(err));
                    });

                } else {
                    patchTasksCalendarEvent(task.event_id, { start_time: newStartIso, end_time: newEndIso });
                    Promise.resolve(updateTaskInDB(realTaskId, { start_time: newStartIso, end_time: newEndIso, date: newDate })).then(invalidateTasksCalendarEvents);
                }
            }
        }
        setDraggingTaskId(null);
        setDragOverCol(null);
        setDragOverLeft(null);
        setDragOffsetX(0);
    };

    return {
        ganttContainerRef, dragOverLeft, currentTimeOffset, isResizing,
        handleDragOver, handleDrop, handleGanttRowDragOver, handleGanttRowDrop,
        handleDragStart, handleDragEnd,
        handleResizeStart, handleResizeMove, handleResizeEnd,
    };
}
